import React, { ReactNode, useEffect, useState } from 'react';
import './Subscribe.scss';
import { Page, PageHeader } from 'components';
import { TextField, Col, Button, ButtonGroup, Modal } from "@cfa/react-components";
import toast from 'react-hot-toast';
import { SubscribeService } from 'services';
import { Row } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IStreamEvent } from 'interfaces';
import { StreamEventItem } from './StreamEventItem';
import { DeviceSearchModal } from './DeviceSearchModal';


export function Subscribe() {

    // check for thingID passed in from devices page
    var defaultTopicEntry = "";
    const searchParams = new URLSearchParams(useLocation().search);
    if (searchParams.has("thingID")) {
        let thing = searchParams.get("thingID");
        defaultTopicEntry = `thing/${thing}/#`;
    }

    const [loading, setLoading] = useState(false);
    const [connected, setConnected] = useState(false);

    const [topicEntry, setTopicEntry] = useState<string>(defaultTopicEntry);
    const [topics, setTopics] = useState<string[]>([]);
    const [events, setEvents] = useState<IStreamEvent[]>([]);

    const [showModal, setShowModal] = useState(false);

    // manage connections
    const connectToStream = () => {
        setLoading(true);

        SubscribeService.connect()
            .then((s) => {
                // handle messages
                s.addEventListener("message", event => {
                    let e = JSON.parse(event.data) as IStreamEvent;
                    setEvents(prev => [...prev, e])
                });

                s.addEventListener("close", event => {
                    toast.success("Disconnected from stream");
                })

                toast.success("Connected to stream");
                setLoading(false);
                setConnected(true);
            })
            .catch(e => {
                setLoading(false);
                setConnected(false);
                toast.error("Not connected to stream");
            });
    }

    const disconnectFromStream = () => {
        SubscribeService.disconnect();
        setConnected(false);
        setTopics([]);
    }

    // disconnect on component unmount
    useEffect( () => () => disconnectFromStream(), [] );

    // add subscriptions
    const subscribeThingIDs = (thingIDs: string[]) => {
        if (thingIDs.length === 0) return;

        let filteredThingIDs = thingIDs.filter(t => !topics.includes(`thing/${t}/#`));

        SubscribeService.subscribe("", filteredThingIDs)
            .then(() => {
                let topics = filteredThingIDs.map(t => `thing/${t}/#`);
                setTopics(prev => {
                    return [...prev, ...topics]
                });
                setTopicEntry("");
                setShowModal(false);
                toast.success("Subscribed to topic");
            })
            .catch(e => {
                toast.error("Failed to subscribe");
            });
    }

    const subscribeManualEntry = () => {
        if (topicEntry === "" || topics.includes(topicEntry)) return;

        SubscribeService.subscribe(topicEntry, [])
            .then(() => {
                setTopics(prev => {
                    return [...prev, topicEntry]
                });
                setTopicEntry("");
                toast.success("Subscribed to topic");

                if (topicEntry === defaultTopicEntry) {
                    defaultTopicEntry = "";
                    history.push('/subscribe');
                }
            })
            .catch(e => {
                toast.error("Failed to subscribe");
            });
    }

    const removeSubscription = (topic: string) => {
        if (!topics.includes(topic)) return;

        SubscribeService.unsubscribe(topic, [])
            .then(() => {
                setTopics(prev => {
                    return prev.filter(t => t != topic);
                });
                setTopicEntry("");
                toast.success("Unsubscribed from topic");

                if (topic === defaultTopicEntry) {
                    defaultTopicEntry = "";
                    history.push('/subscribe');
                }
            })
            .catch(e => {
                toast.error("Failed to unsubscribe");
            });
    }

    const reset = () => {
        setTopicEntry("");
        setTopics([]);
    }

    const history = useHistory();

    const onReset = () => {
        reset();
        if (defaultTopicEntry !== "") {
            defaultTopicEntry = "";
            history.push('/subscribe');
        }
    }

    const closeModal = () => {
        setShowModal(false);
    }

    const subscribeHeader: ReactNode = (
        <div className="devicesList_search">
            <ButtonGroup size='md'>
                <Button onClick={() => setShowModal(true)} disabled={!connected}>
                    <FontAwesomeIcon icon="search"/>
                </Button>
                {!connected && <Button
                    loading={loading}
                    onClick={connectToStream}
                >
                    Connect
                </Button>}
                {connected && <Button
                    loading={loading}
                    onClick={disconnectFromStream}
                >
                    Disconnect
                </Button>}
            </ButtonGroup>
        </div>
    );

    const streamEventsHeader: ReactNode = (
        <div className="devicesList_search">
            <Button size="md" onClick={() => setEvents([])} disabled={loading || events.length === 0}>
                <FontAwesomeIcon icon="window-close"/>
            </Button>
        </div>
    );

    return (<>
        <div className="subscribePage_wrapper">
            <div className="subscribePage_left">
                <Page>
                    <PageHeader title={"Subscribe"} right={subscribeHeader}/>
                    <Row>
                        <Col style={{paddingLeft: 0}}>
                            <TextField
                                fullWidth
                                label="Topic Entry"
                                spellCheck={false}
                                value={topicEntry}
                                onChange={(e) => {
                                    setTopicEntry(e.target.value);
                                }}
                                disabled={!connected}
                            />
                        </Col>
                        <Col lg={4} style={{paddingTop: 24}}>
                            <Button onClick={subscribeManualEntry} className='topicList_iconButton' disabled={!connected}>
                                <FontAwesomeIcon icon="plus"/>
                            </Button>
                        </Col>
                    </Row>
                    {topics.length > 0 && topics.map((t, i) =>(<Row key={i}>
                        <Col style={{paddingLeft: 0}}>
                            <TextField
                                fullWidth
                                spellCheck={false}
                                value={t}
                                disabled
                            />
                        </Col>
                        <Col lg={4}>
                            <Button onClick={() => removeSubscription(t)} className='topicList_iconButton'><FontAwesomeIcon icon="minus" /></Button>
                        </Col>
                    </Row>))}
                </Page>
            </div>
            <div className="subscribePage_right">
                <Page>
                    <PageHeader right={streamEventsHeader}/>
                    <Row>
                        <Col>
                            {events.length > 0 && events.map((t, i) =>(
                                <StreamEventItem key={i} event={t}/>
                            ))}
                        </Col>
                    </Row>
                </Page>
            </div>
        </div>

        <Modal show={showModal} onClose={() => setShowModal(false)} size='xl'>
            <DeviceSearchModal 
                key={1000}
                onSubmit={subscribeThingIDs}
                onHide={closeModal}
            />
        </Modal>
    </>);
}
